import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Table from "components/Table";
import { ReactComponent as DownloadDocument } from "images/profile/document-download.svg";
import ActionTooltip from "components/ActionTooltip";
import AssigneeSubTable from "./AssigneeSubTable";
import EmptyState from "components/EmptyState";
const ProjectsTable = ({
  projects,
  isLoading,
  viewReferences,
  fetchProjects = () => {},
}) => {
  const [t] = useTranslation();
  const columns = useMemo(
    () => [
      {
        id: "expander",
        Header: "",
        Cell: () => null,
        ExpandedRowContent: ({ row }) => {
          const project = row.original;
          return (
            <AssigneeSubTable
              projectRaw={project}
              fetchProjects={fetchProjects}
            />
          );
        },
      },
      {
        Header: t("projectNumberHeader"),
        accessor: "orderNumber",
        Cell: ({ value }) => (
          <div className="font-semibold text-[#1D1B22] text-[16px]">
            {value}
          </div>
        ),
      },
      {
        Header: t("projectStatusHeader"),
        accessor: "projectStatus",
        Cell: ({ value }) => {
          return (
            <div
              className={`flex px-[8px] py-[4px] items-center justify-center gap-[2px] border border-[#BFDBFE] rounded-[6px] bg-[#EFF6FF] `}
            >
              <div className="text-[#3B82F6] font-semibold text-[14px] text-center">
                {t("inProgress")}
              </div>
            </div>
          );
        },
      },
      {
        Header: t("serviceHeader"),
        accessor: "role",
        Cell: ({ value }) => <div className="text-[#1D1B22]">{value}</div>,
      },
      {
        Header: t("totalVolumeHeader"),
        accessor: "unitCount",
        Cell: (props) => {
          const unitCount = props.data[props.row.id]?.unitCount;
          const uom = props.data[props.row.id]?.uom;
          return <div className="text-[#1D1B22]">{`${unitCount} ${uom}`}</div>;
        },
      },
      {
        Header: t("deadlineHeader"),
        accessor: "deadline",
        Cell: ({ value }) => {
          if (!value) return <div className="text-[#666666]">-</div>;

          const date = new Date(value);
          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div className="text-[#1D1B22]">
              <div className="font-medium">{formattedDate}</div>
              <div className="text-[12px] text-[#666666]">{formattedTime}</div>
            </div>
          );
        },
      },
      {
        id: "action",
        Header: "",
        Cell: ({ row }) => (
          <div className="flex items-center gap-[15px]">
            <ActionTooltip label={t("viewReferences")}>
              <button
                onClick={() => viewReferences(row.original)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <DownloadDocument />
              </button>
            </ActionTooltip>
          </div>
        ),
      },
    ],
    [viewReferences]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">{t("loadingProjects")}</div>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return <EmptyState label={t("emptyProjects")} />;
  }

  return (
    <div className="w-full">
      <Table columns={columns} data={projects} />
    </div>
  );
};

ProjectsTable.propTypes = {
  projects: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  viewReferences: PropTypes.func,
  fetchProjects: PropTypes.func,
};

ProjectsTable.defaultProps = {
  isLoading: false,
  viewReferences: () => {},
};

export default ProjectsTable;
